import { useState, useEffect, useMemo, useRef } from 'react';
import { CaretUpDown, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { COUNTRIES, DEFAULT_SUGGESTED, flagUrl } from '../utils/countries';

/**
 * PhoneInput — country-code selector + phone input combo.
 *
 * Props:
 *   value         (string)  full international phone in digits (e.g. "255712345678")
 *   onChange      (fn)      receives the new full phone string
 *   defaultCountry (string) ISO2 code for initial selection if `value` is empty (default "TZ")
 *   placeholder   (string)  placeholder for the local-number input
 *   testIdPrefix  (string)  prefix for data-testid attrs
 *   required      (bool)    HTML required attribute
 */
export default function PhoneInput({
  value = '',
  onChange,
  defaultCountry = 'TZ',
  placeholder = '712 345 678',
  testIdPrefix = 'phone',
  required = false,
}) {
  // Split incoming value into country + local digits whenever it changes from outside
  const splitValue = useMemo(() => {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) return { code: defaultCountry, local: '' };
    // Try to match the longest dial code that prefixes the digits
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of sorted) {
      if (digits.startsWith(c.dial)) {
        return { code: c.code, local: digits.slice(c.dial.length) };
      }
    }
    return { code: defaultCountry, local: digits };
  }, [value, defaultCountry]);

  const [country, setCountry] = useState(splitValue.code);
  const [local, setLocal] = useState(splitValue.local);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);

  // Resync if external `value` changes
  useEffect(() => {
    setCountry(splitValue.code);
    setLocal(splitValue.local);
  }, [splitValue.code, splitValue.local]);

  const selected = useMemo(
    () => COUNTRIES.find(c => c.code === country) || COUNTRIES.find(c => c.code === 'TZ'),
    [country]
  );

  const suggestedList = useMemo(
    () => DEFAULT_SUGGESTED.map(code => COUNTRIES.find(c => c.code === code)).filter(Boolean),
    []
  );

  const otherList = useMemo(() => {
    const set = new Set(suggestedList.map(c => c.code));
    return COUNTRIES.filter(c => !set.has(c.code));
  }, [suggestedList]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [search]);

  // Outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const emit = (newCode, newLocal) => {
    const c = COUNTRIES.find(x => x.code === newCode);
    if (!c) return;
    const cleanLocal = (newLocal || '').replace(/\D/g, '').replace(/^0+/, '');
    onChange?.(cleanLocal ? `${c.dial}${cleanLocal}` : '');
  };

  const handleLocalChange = (e) => {
    const next = e.target.value.replace(/[^\d]/g, '');
    setLocal(next);
    emit(country, next);
  };

  const pickCountry = (code) => {
    setCountry(code);
    setOpen(false);
    setSearch('');
    emit(code, local);
  };

  return (
    <div className="flex h-10 rounded-md border border-[#EDE6D8] bg-transparent shadow-sm focus-within:border-[#0A2540] focus-within:ring-1 focus-within:ring-[#0A2540]/20 transition-colors relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 border-r border-[#EDE6D8] hover:bg-[#FCFAF6] transition-colors text-sm whitespace-nowrap"
        data-testid={`${testIdPrefix}-country-trigger`}
      >
        <img
          src={flagUrl(selected.code)}
          alt={selected.code}
          className="w-5 h-[14px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.04)]"
          loading="eager"
        />
        <span className="text-[#0A2540] font-semibold">+{selected.dial}</span>
        <CaretUpDown size={12} className="text-gray-400" />
      </button>

      <input
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        className="flex-1 min-w-0 px-3 bg-transparent text-gray-900 text-sm placeholder:text-gray-300 focus:outline-none rounded-r-md"
        data-testid={`${testIdPrefix}-local-input`}
        required={required}
      />

      {open && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-[calc(100%+6px)] z-30 w-[360px] max-h-[420px] flex flex-col rounded-xl border border-gray-200 bg-[#FFFFFF] shadow-[0_20px_60px_-15px_rgba(7,121,188,0.25)] overflow-hidden"
          data-testid={`${testIdPrefix}-country-popover`}
        >
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full h-9 pl-8 pr-2 rounded-md bg-gray-50 border border-transparent text-sm focus:outline-none focus:bg-[#FFFFFF] focus:border-[#0A2540]"
                data-testid={`${testIdPrefix}-country-search`}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered === null ? (
              <>
                <SectionLabel>Suggested</SectionLabel>
                {suggestedList.map(c => (
                  <Row key={`s-${c.code}`} c={c} selected={c.code === country} onSelect={() => pickCountry(c.code)} />
                ))}
                <SectionLabel divider>Other countries</SectionLabel>
                {otherList.map(c => (
                  <Row key={`o-${c.code}`} c={c} selected={c.code === country} onSelect={() => pickCountry(c.code)} />
                ))}
              </>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-xs text-gray-400">No countries match "{search}"</div>
            ) : (
              filtered.map(c => (
                <Row key={`f-${c.code}`} c={c} selected={c.code === country} onSelect={() => pickCountry(c.code)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children, divider }) {
  return (
    <div className={`px-3 pt-3 pb-1.5 ${divider ? 'border-t border-gray-100 mt-1' : ''}`}>
      <p className="text-[10px] font-semibold tracking-[0.15em] uppercase text-gray-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {children}
      </p>
    </div>
  );
}

function Row({ c, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm transition-colors ${
        selected ? 'bg-[#0A2540]/8 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
      }`}
      data-testid={`country-option-${c.code}`}
    >
      <span className="flex items-center gap-2.5 truncate min-w-0">
        <span className="w-4 flex items-center justify-center shrink-0">
          {selected ? <Check size={12} weight="bold" className="text-[#0A2540]" /> : null}
        </span>
        <img
          src={flagUrl(c.code)}
          alt={c.code}
          className="w-5 h-[14px] object-cover rounded-[2px] shadow-[0_0_0_1px_rgba(0,0,0,0.04)] shrink-0"
          loading="lazy"
        />
        <span className="truncate">{c.name}</span>
      </span>
      <span className="font-mono text-xs text-gray-500 shrink-0">+{c.dial}</span>
    </button>
  );
}
